import type { Language } from "../types";
import type { TextSchema } from "../i18n";
import { textMap } from "../i18n";

function ensureMetaByName(name: string): HTMLMetaElement {
  let el = document.querySelector(
    `meta[name="${CSS.escape(name)}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
}

function ensureMetaProperty(property: string): HTMLMetaElement {
  let el = document.querySelector(
    `meta[property="${CSS.escape(property)}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  return el;
}

function ensureMetaHttpEquiv(header: string): HTMLMetaElement {
  let el = document.querySelector(
    `meta[http-equiv="${CSS.escape(header)}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("http-equiv", header);
    document.head.appendChild(el);
  }
  return el;
}

function ensureLinkById(id: string, rel: string): HTMLLinkElement {
  let el = document.getElementById(id) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.id = id;
    el.rel = rel;
    document.head.appendChild(el);
  }
  return el;
}

function clearOgLocaleAlternates(): void {
  document
    .querySelectorAll('meta[property="og:locale:alternate"]')
    .forEach((n) => n.remove());
}

/**
 * 优先 VITE_SITE_URL（生产见 `.env.production`，默认 https://xtool.chat）；
 * 未设置时用当前页 origin（本地开发一般为 http://127.0.0.1:5200）。
 */
export function siteOriginForSeo(): string {
  const fromEnv = import.meta.env.VITE_SITE_URL?.trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, "");
  }
  if (typeof window === "undefined") {
    return "";
  }
  return window.location.origin;
}

function siteHostname(): string {
  try {
    const u = new URL(siteOriginForSeo() || "https://xtool.chat");
    return u.hostname === "xtool.chat" ? "" : u.hostname;
  } catch {
    return "";
  }
}

function buildStructuredData(
  origin: string,
  lang: Language,
  t: TextSchema,
  canonicalUrl: string,
): Record<string, unknown> {
  const base = origin.replace(/\/$/, "");
  const orgId = `${base}/#organization`;
  const webSiteId = `${base}/#website`;
  const appId = `${base}/#software`;
  const logoUrl = `${base}/x-tool-logo.png`;
  const inLang = lang === "zh" ? "zh-CN" : "en-US";

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: t.seoOgSiteName,
        url: `${base}/`,
        logo: {
          "@type": "ImageObject",
          url: logoUrl,
          caption: t.seoOgImageAlt,
        },
      },
      {
        "@type": "WebSite",
        "@id": webSiteId,
        url: `${base}/`,
        name: t.seoOgSiteName,
        alternateName: [t.productName, "xtool.chat"],
        description: t.seoMetaDescription,
        inLanguage: ["zh-CN", "en-US"],
        publisher: { "@id": orgId },
        isFamilyFriendly: true,
      },
      {
        "@type": "SoftwareApplication",
        "@id": appId,
        name: t.productName,
        alternateName: t.seoOgSiteName,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        browserRequirements: "Requires JavaScript. Modern evergreen browser.",
        description: t.seoMetaDescription,
        url: canonicalUrl,
        image: logoUrl,
        featureList: t.seoSchemaFeatures,
        publisher: { "@id": orgId },
        isAccessibleForFree: true,
        inLanguage: inLang,
      },
    ],
  };
}

function syncJsonLd(payload: Record<string, unknown>): void {
  const id = "xtool-structured-data";
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

const THEME_COLOR = "#246BFD";

/**
 * 按当前界面语言同步 document、meta、Open Graph、Twitter、JSON-LD、canonical 与 hreflang。
 */
export function syncDocumentSeo(lang: Language): void {
  if (typeof document === "undefined") {
    return;
  }

  const t = textMap[lang];
  const htmlLang = lang === "zh" ? "zh-CN" : "en";
  document.documentElement.lang = htmlLang;

  document.title = t.seoDocumentTitle;

  const desc =
    document.querySelector('meta[name="description"]') ??
    (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
      return m;
    })();
  desc.setAttribute("content", t.seoMetaDescription);

  ensureMetaByName("keywords").setAttribute("content", t.seoKeywords);
  ensureMetaByName("author").setAttribute("content", t.seoOgSiteName);
  ensureMetaByName("publisher").setAttribute("content", t.seoOgSiteName);
  ensureMetaByName("application-name").setAttribute("content", t.seoOgSiteName);
  ensureMetaByName("apple-mobile-web-app-title").setAttribute(
    "content",
    t.seoDocumentTitle,
  );
  ensureMetaByName("theme-color").setAttribute("content", THEME_COLOR);
  ensureMetaByName("msapplication-TileColor").setAttribute("content", THEME_COLOR);
  ensureMetaByName("color-scheme").setAttribute("content", "light dark");
  ensureMetaByName("referrer").setAttribute(
    "content",
    "strict-origin-when-cross-origin",
  );
  ensureMetaByName("format-detection").setAttribute(
    "content",
    "telephone=no",
  );
  ensureMetaByName("robots").setAttribute(
    "content",
    "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  );
  ensureMetaByName("googlebot").setAttribute(
    "content",
    "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  );

  ensureMetaHttpEquiv("content-language").setAttribute("content", htmlLang);

  const origin = siteOriginForSeo();
  const path =
    window.location.pathname.replace(/\/$/, "") || "/";
  const search = window.location.search || "";
  const canonicalUrl = `${origin}${path === "/" ? "/" : path}${search}`;

  ensureMetaProperty("og:type").setAttribute("content", "website");
  ensureMetaProperty("og:site_name").setAttribute("content", t.seoOgSiteName);
  ensureMetaProperty("og:title").setAttribute("content", t.seoDocumentTitle);
  ensureMetaProperty("og:description").setAttribute("content", t.seoMetaDescription);
  ensureMetaProperty("og:url").setAttribute("content", canonicalUrl);
  ensureMetaProperty("og:locale").setAttribute(
    "content",
    lang === "zh" ? "zh_CN" : "en_US",
  );
  clearOgLocaleAlternates();
  const altLocale = document.createElement("meta");
  altLocale.setAttribute("property", "og:locale:alternate");
  altLocale.setAttribute("content", lang === "zh" ? "en_US" : "zh_CN");
  document.head.appendChild(altLocale);

  const ogImage = `${origin}/x-tool-logo.png`;
  ensureMetaProperty("og:image").setAttribute("content", ogImage);
  ensureMetaProperty("og:image:secure_url").setAttribute("content", ogImage);
  ensureMetaProperty("og:image:type").setAttribute("content", "image/png");
  ensureMetaProperty("og:image:alt").setAttribute("content", t.seoOgImageAlt);

  ensureMetaByName("twitter:card").setAttribute("content", "summary_large_image");
  ensureMetaByName("twitter:title").setAttribute("content", t.seoDocumentTitle);
  ensureMetaByName("twitter:description").setAttribute(
    "content",
    t.seoMetaDescription,
  );
  ensureMetaByName("twitter:image").setAttribute("content", ogImage);
  ensureMetaByName("twitter:image:alt").setAttribute("content", t.seoOgImageAlt);
  const host = siteHostname();
  if (host) {
    ensureMetaByName("twitter:domain").setAttribute("content", host);
  }
  ensureMetaByName("twitter:url").setAttribute("content", canonicalUrl);

  ensureLinkById("xtool-link-canonical", "canonical").setAttribute(
    "href",
    canonicalUrl,
  );

  const setHreflang = (id: string, hreflang: string, href: string) => {
    const el = ensureLinkById(id, "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
  };
  setHreflang("xtool-hreflang-zh", "zh-CN", canonicalUrl);
  setHreflang("xtool-hreflang-en", "en", canonicalUrl);
  setHreflang("xtool-hreflang-x-default", "x-default", canonicalUrl);

  if (origin) {
    syncJsonLd(buildStructuredData(origin, lang, t, canonicalUrl));
  }
}
